import React, { ReactNode, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { SvgIcon, IconTypes } from 'components/helpers/SvgIcon';
import styles from './MessageBanner.module.scss';

export interface MessageBannerProps {
  type: 'error' | 'success';
  message: ReactNode;
  className?: string;
  role?: string;
  autoFocus?: boolean;
  icon?: IconTypes;
  'data-testid'?: string;
}

export default function MessageBanner({
  type,
  message,
  className,
  role = 'alert',
  autoFocus = false,
  icon = 'info',
  'data-testid': dataTestId,
}: MessageBannerProps): JSX.Element {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus && bannerRef.current) {
      bannerRef.current.focus();
    }
  }, [autoFocus]);

  // Split message if it's a string with '|' separator
  const renderMessage = () => {
    if (typeof message === 'string' && message.includes('|')) {
      const [mainMessage, subMessage] = message.split('|');
      return (
        <div className={styles.messageWrapper}>
          <span className={styles.message}>{mainMessage}</span>
          {subMessage && <span className={styles.subMessage}>{subMessage.trim()}</span>}
        </div>
      );
    }
    return <span className={styles.message}>{message}</span>;
  };

  return (
    <div
      ref={bannerRef}
      className={clsx(styles.messageBanner, styles[type], className)}
      role={role}
      tabIndex={autoFocus ? -1 : undefined}
      data-testid={dataTestId}
    >
      <SvgIcon icon={icon} size="sm" className={styles.icon} />
      {renderMessage()}
    </div>
  );
}
